import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNotification } from '../../../contexts/NotificationContext';
import { contentService, mediaService, buildImageUrl } from '../../../services/api';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #1f2937;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
`;

const Description = styled.p`
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Section = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  color: #1f2937;
  margin-bottom: 1rem;
  font-size: 1.125rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #2563eb;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewButton = styled.button`
  background: #6b7280;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #4b5563;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`;

const ImagePreview = styled.div`
  margin-top: 0.5rem;
  position: relative;
  display: inline-block;
  
  img {
    max-width: 200px;
    max-height: 120px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }
`;

const ImageUploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #e5e7eb;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  margin-top: 0.5rem;
  
  &:hover {
    background: #d1d5db;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #dc2626;
  }
`;

const AboutSettings: React.FC = () => {
  const [formData, setFormData] = useState({
    about_title: '',
    about_content: '',
    about_image: '',
    about_mission: '',
    about_vision: '',
    about_values: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await contentService.getSiteSettings();
      
      setFormData({
        about_title: response.about_title || 'Sobre Nós',
        about_content: response.about_content || '',
        about_image: response.about_image || '',
        about_mission: response.about_mission || '',
        about_vision: response.about_vision || '',
        about_values: response.about_values || ''
      });
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      showNotification('Erro ao carregar configurações da página Sobre', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      await contentService.updateSiteSettings(formData);
      
      showNotification('Configurações da página Sobre salvas com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      showNotification('Erro ao salvar configurações da página Sobre', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open('/sobre', '_blank');
  };
  
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      const response = await mediaService.uploadImage(file);
      handleInputChange('about_image', response.url);
      showNotification('Imagem enviada com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao enviar imagem:', error);
      showNotification('Erro ao enviar imagem', 'error');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <LoadingMessage>Carregando configurações...</LoadingMessage>;
  }

  return (
    <Container>
      <Title>Configuração da Página Sobre</Title>
      <Description>
        Configure o conteúdo da página "Sobre Nós" da sua imobiliária. 
        Personalize os textos para refletir a identidade e valores da sua empresa.
      </Description>

      <Form onSubmit={handleSave}>
        <Section>
          <SectionTitle>Informações Básicas</SectionTitle>
          
          <FormGroup>
            <Label>Título da Página</Label>
            <Input
              type="text"
              value={formData.about_title}
              onChange={(e) => handleInputChange('about_title', e.target.value)}
              placeholder="Ex: Sobre Nós"
            />
          </FormGroup>

          <FormGroup>
            <Label>Imagem Principal</Label>
            {formData.about_image ? (
              <ImagePreview>
                <img 
                  src={formData.about_image.startsWith('http') ? formData.about_image : buildImageUrl(formData.about_image)} 
                  alt="Preview" 
                />
                <RemoveImageButton 
                  type="button"
                  onClick={() => handleInputChange('about_image', '')}
                >
                  ✕
                </RemoveImageButton>
              </ImagePreview>
            ) : (
              <ImageUploadButton>
                📷 Escolher Imagem
                <HiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </ImageUploadButton>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Texto Principal</Label>
            <TextArea
              value={formData.about_content}
              onChange={(e) => handleInputChange('about_content', e.target.value)}
              placeholder="Descreva sua imobiliária, história, experiência..."
              rows={6}
            />
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Missão, Visão e Valores</SectionTitle>
          
          <FormGroup>
            <Label>Nossa Missão</Label>
            <TextArea
              value={formData.about_mission}
              onChange={(e) => handleInputChange('about_mission', e.target.value)}
              placeholder="Qual é a missão da sua imobiliária?"
              rows={3}
            />
          </FormGroup>

          <FormGroup>
            <Label>Nossa Visão</Label>
            <TextArea
              value={formData.about_vision}
              onChange={(e) => handleInputChange('about_vision', e.target.value)}
              placeholder="Qual é a visão de futuro da sua empresa?"
              rows={3}
            />
          </FormGroup>

          <FormGroup>
            <Label>Nossos Valores</Label>
            <TextArea
              value={formData.about_values}
              onChange={(e) => handleInputChange('about_values', e.target.value)}
              placeholder="Quais são os valores que guiam sua empresa?"
              rows={3}
            />
          </FormGroup>
        </Section>

        <ButtonGroup>
          <SaveButton type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </SaveButton>
          <PreviewButton type="button" onClick={handlePreview}>
            Visualizar Página
          </PreviewButton>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default AboutSettings;